import { usePreviewSimRunQuery } from "@/api/previewSim";
import {
  SIM_LANGUAGE_LABELS,
  SIM_STATUS_LABELS,
  describeEndReason,
  totalSimStats,
} from "@/lib/previewSim";
import { Badge } from "@/ui/Badge";
import { Card, CardBody, CardHeader, CardTitle } from "@/ui/Card";

/**
 * Patient-simulator stats for a run that was driven by one: status, end
 * reason, turns and the simulator's own per-turn latency, token counts and
 * model.
 *
 * The status is read for EVERY run — the service answers the one generic 404
 * for a preview that is not a simulated run, which `usePreviewSimRunQuery`
 * reports as `null` — and the panel renders nothing for it. Body-free by
 * contract: counts, timings and ids only, never a transcript line.
 */
export function SimStatsPanel({
  projectId,
  previewRunRef,
  enabled,
}: {
  projectId: string;
  previewRunRef: string;
  /** False without `preview.inspect`: the read is the same gate as the run page. */
  enabled: boolean;
}) {
  const sim = usePreviewSimRunQuery(projectId, previewRunRef, { enabled });
  const run = sim.data;
  if (!run) {
    return null;
  }
  const totals = totalSimStats(run.simStats);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Patient simulator</CardTitle>
      </CardHeader>
      <CardBody className="space-y-4">
        <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Status
            </dt>
            <dd>
              <Badge tone={run.status === "failed" ? "destructive" : "outline"}>
                {SIM_STATUS_LABELS[run.status]}
              </Badge>
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Ended
            </dt>
            <dd>{describeEndReason(run.endReason)}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Turns
            </dt>
            <dd dir="ltr">
              {run.turnsCompleted} of {run.maxTurns}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Persona
            </dt>
            <dd>
              <span dir="ltr">{run.personaId}</span> ·{" "}
              {SIM_LANGUAGE_LABELS[run.language]}
            </dd>
          </div>
        </dl>

        {run.error ? (
          <p role="alert" className="text-sm text-destructive">
            {run.error}
          </p>
        ) : null}

        {run.simStats.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            The simulator has reported no per-turn stats yet.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <caption className="sr-only">Per-turn simulator stats</caption>
              <thead>
                <tr className="text-start text-xs uppercase tracking-wide text-muted-foreground">
                  <th scope="col" className="px-3 py-2 font-medium">
                    Turn
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Latency
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Prompt tokens
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Completion tokens
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Model
                  </th>
                </tr>
              </thead>
              <tbody dir="ltr">
                {run.simStats.map((stat) => (
                  <tr key={stat.turn} className="border-t border-border">
                    <th scope="row" className="px-3 py-2 text-start font-normal">
                      {stat.turn}
                    </th>
                    <td className="px-3 py-2">{stat.latencyMs} ms</td>
                    <td className="px-3 py-2">{stat.promptTokens ?? "—"}</td>
                    <td className="px-3 py-2">{stat.completionTokens ?? "—"}</td>
                    <td className="px-3 py-2">{stat.model}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot dir="ltr">
                <tr className="border-t border-border text-muted-foreground">
                  <th scope="row" className="px-3 py-2 text-start font-medium">
                    All turns
                  </th>
                  <td className="px-3 py-2">
                    {totals.meanLatencyMs === null
                      ? "—"
                      : `${totals.meanLatencyMs} ms mean`}
                  </td>
                  <td className="px-3 py-2">{totals.promptTokens ?? "—"}</td>
                  <td className="px-3 py-2">{totals.completionTokens ?? "—"}</td>
                  <td className="px-3 py-2">{totals.models.join(", ") || "—"}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
